import type { LineEntry } from '../types/LineEntry';
import { normalizeQuery } from './text';

/** A chip in the exclude widget: either a mode filter or a specific line. */
export type Chip = { kind: 'mode'; name: string } | { kind: 'line'; name: string; mode: string };

/** One row in {@link MODES}: display name + canonical short token used on the wire. */
interface ModeEntry {
  name: string;
  token: string;
}

/**
 * Single source of truth for supported exclude modes: the display name plus
 * the canonical short token the backend's `parse_mode` accepts. Everything
 * else in this module derives from this list.
 */
const MODES: ModeEntry[] = [
  { name: 'S-Bahn', token: 's-bahn' },
  { name: 'U-Bahn', token: 'u-bahn' },
  { name: 'Bus', token: 'bus' },
  { name: 'Tram', token: 'tram' },
  { name: 'Regional', token: 'regional' },
  { name: 'Ferry', token: 'ferry' },
];

/** Modes shown as chips regardless of the catalog's line coverage. */
const EXCLUDE_MODE_CATALOG: { name: string }[] = MODES.map(({ name }) => ({ name }));

/** Max chips returned by {@link filterExcludeSuggestions}. */
export const EXCLUDE_MAX_SUGGESTIONS = 8;

/**
 * Canonical short forms the backend's `parse_mode` accepts. Keeps the URL
 * stable across page loads and independent of the label the UI shows.
 */
const EXCLUDE_MODE_TOKENS: Record<string, string> = Object.fromEntries(
  MODES.map(({ name, token }) => [name, token]),
);

/** Inverse of {@link EXCLUDE_MODE_TOKENS}; case-normalized keys. */
const EXCLUDE_MODE_FROM_TOKEN: Record<string, string> = Object.fromEntries(
  MODES.map(({ name, token }) => [token, name]),
);

/** Stable identity string; `mode:<name>` for mode chips, `line:<name>|<mode>` for lines; `''` for null/undefined. */
export function chipKey(chip: Chip | null | undefined): string {
  if (!chip) return '';
  if (chip.kind === 'mode') return `mode:${chip.name}`;
  return `line:${chip.name}|${chip.mode}`;
}

/** Display label for a chip (chip name for either kind); `''` for null/undefined. */
export function chipLabel(chip: Chip | null | undefined): string {
  return chip ? chip.name : '';
}

/**
 * True when `text` contains `query` as a substring (case-insensitive on `text`).
 * `query` MUST already be lower-cased (pass through {@link normalizeQuery} first).
 * Empty `query` matches everything; nullish `text` never matches.
 */
export function isQueryMatch(text: string | null | undefined, query: string): boolean {
  if (!query) return true;
  if (text === null || text === undefined) return false;
  return text.toLowerCase().includes(query);
}

/** Pre-lowered entry — computed once per catalog to avoid per-keystroke allocs. */
interface PreparedLine {
  entry: LineEntry;
  nameLower: string;
  modeLower: string;
}

interface PreparedCatalog {
  lines: PreparedLine[];
  /** name → entry, used by {@link parseLinesCsv} to back-fill mode. */
  byName: Map<string, LineEntry>;
}

const preparedCatalogs = new WeakMap<LineEntry[], PreparedCatalog>();

function prepareCatalog(catalog: LineEntry[]): PreparedCatalog {
  const cached = preparedCatalogs.get(catalog);
  if (cached) return cached;
  const lines: PreparedLine[] = [];
  const byName = new Map<string, LineEntry>();
  for (const entry of catalog) {
    if (!entry.name) continue;
    lines.push({
      entry,
      nameLower: entry.name.toLowerCase(),
      modeLower: entry.mode.toLowerCase(),
    });
    byName.set(entry.name, entry);
  }
  const prepared: PreparedCatalog = { lines, byName };
  preparedCatalogs.set(catalog, prepared);
  return prepared;
}

function isModeSuggestable(
  name: string,
  query: string,
  excludedKeys: ReadonlySet<string>,
): boolean {
  if (!isQueryMatch(name, query)) return false;
  return !excludedKeys.has(chipKey({ kind: 'mode', name }));
}

/** Mode chips matching `query` and not present in `excludedKeys`. */
export function suggestModes(query: string, excludedKeys: ReadonlySet<string>): Chip[] {
  return EXCLUDE_MODE_CATALOG.filter(({ name }) =>
    isModeSuggestable(name, query, excludedKeys),
  ).map(({ name }) => ({ kind: 'mode', name }));
}

function isLineMatch(prepared: PreparedLine, query: string): boolean {
  if (!query) return true;
  return prepared.nameLower.includes(query) || prepared.modeLower.includes(query);
}

function tryLineChip(
  line: PreparedLine,
  query: string,
  excludedKeys: ReadonlySet<string>,
): Chip | undefined {
  if (!isLineMatch(line, query)) return undefined;
  const chip: Chip = { kind: 'line', name: line.entry.name, mode: line.entry.mode };
  if (excludedKeys.has(chipKey(chip))) return undefined;
  return chip;
}

function tryPushLineChip(
  line: PreparedLine,
  query: string,
  excludedKeys: ReadonlySet<string>,
  output: Chip[],
): void {
  const chip = tryLineChip(line, query, excludedKeys);
  if (chip !== undefined) output.push(chip);
}

function collectLineChips(
  prepared: PreparedCatalog,
  query: string,
  excludedKeys: ReadonlySet<string>,
  budget: number,
): Chip[] {
  const output: Chip[] = [];
  for (const line of prepared.lines) {
    if (output.length >= budget) break;
    tryPushLineChip(line, query, excludedKeys, output);
  }
  return output;
}

/**
 * Line chips from `linesCatalog` matching `query` (against name or mode) and
 * not present in `excludedKeys`. Skips malformed catalog entries. Stops after
 * `budget` matches so the caller does not walk the whole catalog when only a
 * few results are needed.
 */
export function suggestLines(
  linesCatalog: LineEntry[] | null | undefined,
  query: string,
  excludedKeys: ReadonlySet<string>,
  budget: number = Infinity,
): Chip[] {
  if (!linesCatalog || budget <= 0) return [];
  const prepared = prepareCatalog(linesCatalog);
  return collectLineChips(prepared, query, excludedKeys, budget);
}

/** Builds a chip-key set from a raw chip list; used by tests + ad-hoc callers who don't own an {@link ExcludeStore}. */
export function chipKeysOf(excludedChips: Chip[] | null | undefined): ReadonlySet<string> {
  const source = excludedChips ?? [];
  const keys = new Set<string>();
  for (const chip of source) keys.add(chipKey(chip));
  return keys;
}

/**
 * Up to {@link EXCLUDE_MAX_SUGGESTIONS} chips matching `query`, with modes
 * listed first, then lines. Entries whose key is in `excludedKeys` are omitted;
 * pass an empty {@link Set} (or the store's `excludedKeys` getter) to filter nothing.
 */
export function filterExcludeSuggestions(
  linesCatalog: LineEntry[] | null | undefined,
  query: string | number | null | undefined,
  excludedKeys: ReadonlySet<string>,
): Chip[] {
  const normalized = normalizeQuery(query);
  const modes = suggestModes(normalized, excludedKeys);
  const lineBudget = EXCLUDE_MAX_SUGGESTIONS - modes.length;
  const lines = suggestLines(linesCatalog, normalized, excludedKeys, lineBudget);
  return [...modes, ...lines];
}

function appendChipTokens(chip: Chip, modes: string[], lines: string[]): void {
  if (chip.kind === 'mode') {
    const token = EXCLUDE_MODE_TOKENS[chip.name];
    if (token) modes.push(token);
    return;
  }
  if (chip.name) lines.push(chip.name);
}

/**
 * Serializes `chips` into a `{modes, lines}` CSV pair. Modes emit canonical
 * short tokens; line names pass through verbatim. Both CSVs are locale-sorted
 * so chip order does not affect the output. Inverse of {@link parseBlockParameters}.
 */
export function buildBlockParameters(chips: Chip[] | null | undefined): {
  modes: string;
  lines: string;
} {
  const modes: string[] = [];
  const lines: string[] = [];
  const chipList = chips ?? [];
  for (const chip of chipList) appendChipTokens(chip, modes, lines);
  const sortedModes = modes.toSorted((first, second) => first.localeCompare(second));
  const sortedLines = lines.toSorted((first, second) => first.localeCompare(second));
  return { modes: sortedModes.join(','), lines: sortedLines.join(',') };
}

function collectModeChip(rawToken: string, seen: Set<string>, output: Chip[]): void {
  const token = rawToken.trim().toLowerCase();
  const label = EXCLUDE_MODE_FROM_TOKEN[token];
  if (!label) return;
  const chip: Chip = { kind: 'mode', name: label };
  const keyValue = chipKey(chip);
  if (seen.has(keyValue)) return;
  seen.add(keyValue);
  output.push(chip);
}

/** Parses a mode CSV back into chips, dropping unknown tokens and dedup'ing. Inverse of the `modes` half of {@link buildBlockParameters}. */
export function parseModesCsv(csvText: string | null | undefined): Chip[] {
  const output: Chip[] = [];
  const seen = new Set<string>();
  const rawTokens = (csvText ?? '').split(',');
  for (const rawToken of rawTokens) collectModeChip(rawToken, seen, output);
  return output;
}

function collectLineChip(
  rawName: string,
  byName: Map<string, LineEntry>,
  seen: Set<string>,
  output: Chip[],
): void {
  const name = rawName.trim();
  if (!name) return;
  const match = byName.get(name);
  const chip: Chip = { kind: 'line', name, mode: match ? match.mode : '' };
  const keyValue = chipKey(chip);
  if (seen.has(keyValue)) return;
  seen.add(keyValue);
  output.push(chip);
}

/**
 * Parses a line-name CSV back into chips, back-filling mode from `linesCatalog`
 * when possible (empty mode for unknown names). Dedupes by (name, mode).
 * Inverse of the `lines` half of {@link buildBlockParameters}.
 */
export function parseLinesCsv(
  csvText: string | null | undefined,
  linesCatalog: LineEntry[] | null | undefined,
): Chip[] {
  const byName = linesCatalog ? prepareCatalog(linesCatalog).byName : new Map<string, LineEntry>();
  const output: Chip[] = [];
  const seen = new Set<string>();
  const rawTokens = (csvText ?? '').split(',');
  for (const rawName of rawTokens) collectLineChip(rawName, byName, seen, output);
  return output;
}

/**
 * Reconstructs the chip list from URL query params. Modes come first so the
 * visual order matches {@link filterExcludeSuggestions}. Inverse of
 * {@link buildBlockParameters}.
 */
export function parseBlockParameters(
  modesCsv: string | null | undefined,
  linesCsv: string | null | undefined,
  linesCatalog: LineEntry[] | null | undefined,
): Chip[] {
  return [...parseModesCsv(modesCsv), ...parseLinesCsv(linesCsv, linesCatalog)];
}

export { normalizeQuery } from './text';
